import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { juntarClasses } from './classes';
import { IconeFechar } from './icones';
import { alvoDoPortal } from './portal';

/** Uma opção marcada, como aparece no campo: o nome e o "remover". */
export interface FiltroMarcado {
  readonly rotulo: string;
  readonly tirar: () => void;
}

/**
 * O campo "Filtro" das listas: as opções marcadas aparecem no próprio campo,
 * cada uma com o seu "remover", e o clique abre um painel com as caixas de
 * marcar — o que cada lista quiser (`GrupoDeFiltro`, `OpcaoDeFiltro`, ou um
 * campo de valor, como o "Créditos acima de:" da Prospecção).
 *
 * Nasceu no filtro da Prospecção e foi para o kit quando a tela de Empresas
 * trocou as abas de perfil pelo mesmo filtro: o comportamento do painel é um
 * só nas duas.
 *
 * Como a seleção múltipla (`CampoDeSelecaoMultipla`), o painel vai para o
 * `body` (ou para o modal aberto), ancorado pela posição do campo, e fecha ao
 * clicar fora, ao rolar a página, com Escape ou quando o foco sai dele. Ao
 * abrir, o foco vai para a primeira caixa: pelo teclado, já dá para marcar.
 */
export function CampoDeFiltro(props: {
  /** Padrão: "Filtro". */
  readonly rotulo?: string;
  /** O rótulo só para leitor de tela — quando o campo fica numa barra sem rótulos. */
  readonly rotuloOculto?: boolean;
  readonly marcados: readonly FiltroMarcado[];
  /** O que o campo diz sem nada marcado — "Todas as empresas". */
  readonly semFiltro: string;
  readonly aoLimpar: () => void;
  /** O painel: grupos e opções. */
  readonly children: ReactNode;
  readonly className?: string;
}): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const [posicao, setPosicao] = useState<{ top: number; left: number; width: number } | null>(null);
  const id = useId();
  const idPainel = `${id}-painel`;
  // As etiquetas mudam a altura do campo: o painel acompanha.
  const assinatura = props.marcados.map((m) => m.rotulo).join('|');

  const fechar = (devolverFoco: boolean): void => {
    setAberto(false);
    if (devolverFoco) campo.current?.focus();
  };

  useLayoutEffect(() => {
    if (!aberto || campo.current === null) return;
    const r = campo.current.getBoundingClientRect();
    setPosicao({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 288) });
  }, [aberto, assinatura]);

  useEffect(() => {
    if (!aberto) return;
    painel.current?.querySelector<HTMLInputElement>('input')?.focus();
    const aoClicarFora = (ev: MouseEvent): void => {
      const alvo = ev.target as Node;
      if (raiz.current?.contains(alvo) || painel.current?.contains(alvo)) return;
      setAberto(false);
    };
    const aoRolar = (ev: Event): void => {
      if (painel.current?.contains(ev.target as Node)) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    window.addEventListener('scroll', aoRolar, true);
    window.addEventListener('resize', aoRolar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      window.removeEventListener('scroll', aoRolar, true);
      window.removeEventListener('resize', aoRolar);
    };
  }, [aberto]);

  const algumMarcado = props.marcados.length > 0;

  return (
    <div ref={raiz} className={juntarClasses('relative flex flex-col gap-1.5', props.className)}>
      <span
        id={id}
        className={
          props.rotuloOculto === true ? 'sr-only' : 'text-sm font-medium text-conteudo-secundario'
        }
      >
        {props.rotulo ?? 'Filtro'}
      </span>

      <div
        ref={campo}
        role="button"
        tabIndex={0}
        aria-labelledby={id}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-controls={idPainel}
        className="flex min-h-11 w-full cursor-pointer flex-wrap items-center gap-1.5 rounded-card border border-borda bg-superficie-elevada px-3 py-1.5 transition-colors hover:border-acento-ciano/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acento-ciano focus-visible:ring-offset-2 focus-visible:ring-offset-superficie-base"
        onClick={() => setAberto((a) => !a)}
        onKeyDown={(ev) => {
          if (ev.key === 'Escape') fechar(false);
          if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'ArrowDown') {
            ev.preventDefault();
            setAberto(true);
          }
        }}
      >
        {algumMarcado ? (
          props.marcados.map((m) => (
            <span
              key={m.rotulo}
              className="inline-flex items-center gap-1 rounded-full border border-borda bg-superficie-base px-2.5 py-0.5 text-xs text-conteudo-primario"
            >
              {m.rotulo}
              <button
                type="button"
                aria-label={`Tirar o filtro ${m.rotulo}`}
                className="rounded-full text-conteudo-secundario hover:text-conteudo-primario focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-acento-ciano"
                onClick={(ev) => {
                  ev.stopPropagation();
                  m.tirar();
                }}
              >
                <IconeFechar className="h-3 w-3" />
              </button>
            </span>
          ))
        ) : (
          <span className="text-sm text-conteudo-primario">{props.semFiltro}</span>
        )}
        <span aria-hidden="true" className="ml-auto text-xs text-conteudo-sutil">
          {aberto ? '▲' : '▼'}
        </span>
      </div>

      {aberto &&
        createPortal(
          <div
            ref={painel}
            id={idPainel}
            role="dialog"
            aria-labelledby={id}
            style={{
              position: 'fixed',
              top: posicao?.top ?? -9999,
              left: posicao?.left ?? -9999,
              width: posicao?.width ?? 288,
              zIndex: 60,
            }}
            className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto rounded-card border border-borda bg-superficie-elevada p-3 shadow-cartao"
            onKeyDown={(ev) => {
              if (ev.key === 'Escape') {
                ev.stopPropagation();
                fechar(true);
              }
            }}
            onBlur={(ev) => {
              // O foco saiu do painel (Tab depois do último item): fecha.
              const destino = ev.relatedTarget as Node | null;
              if (destino === null) return;
              if (painel.current?.contains(destino) || raiz.current?.contains(destino)) return;
              setAberto(false);
            }}
          >
            {props.children}

            {algumMarcado && (
              <button
                type="button"
                className="self-start text-xs text-conteudo-sutil underline-offset-2 hover:text-conteudo-primario hover:underline"
                onClick={props.aoLimpar}
              >
                Limpar filtro
              </button>
            )}
          </div>,
          alvoDoPortal(raiz.current),
        )}
    </div>
  );
}

/** Um grupo de opções do painel, com título e, se preciso, como elas se combinam. */
export function GrupoDeFiltro(props: {
  readonly titulo: string;
  readonly nota?: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <fieldset className="flex flex-col gap-0.5">
      <legend className="mb-1 text-xs font-medium uppercase tracking-wide text-conteudo-sutil">
        {props.titulo}
      </legend>
      {props.children}
      {props.nota !== undefined && (
        <p className="px-2 pt-0.5 text-xs text-conteudo-sutil">{props.nota}</p>
      )}
    </fieldset>
  );
}

/** Uma caixa de marcar do painel — com a contagem ao lado, quando a lista a tem. */
export function OpcaoDeFiltro(props: {
  readonly rotulo: string;
  readonly marcada: boolean;
  readonly aoMudar: (marcada: boolean) => void;
  readonly contagem?: number;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-card px-2 py-1.5 text-sm text-conteudo-primario hover:bg-superficie-vidro">
      <input
        type="checkbox"
        className="h-4 w-4 accent-acento-indigo"
        checked={props.marcada}
        onChange={(ev) => props.aoMudar(ev.target.checked)}
      />
      <span className="flex-1">{props.rotulo}</span>
      {props.contagem !== undefined && (
        <span className="text-xs tabular-nums text-conteudo-sutil">{props.contagem}</span>
      )}
    </label>
  );
}
